import { useAnalytics } from '@newfront-insurance/data-layer-client';

import { useAdminTRPC } from '../../../shared/providers/trpc';

export const FILE_PREVIEW_ANALYTICS_EVENT = 'File - preview';

export interface DownloadFileArgs {
  fileUuid: string;
  filename?: string;
}

interface UseDownloadFileOptions {
  onPreviewEmailLogger?: (fileUuid: string) => void;
}

export function useDownloadFile(options?: UseDownloadFileOptions) {
  const { useMutation } = useAdminTRPC();
  const { mutateAsync, isLoading } = useMutation('files.downloadFile');
  const analytics = useAnalytics();

  return {
    /**
     * Downloads file by the given file uuid
     *
     * @param fileUuid the file uuid
     * @param filename an optional filename, defaults to the original filename
     */
    download: async (fileUuid: string, filename?: string) => {
      // eslint-disable-next-line no-void
      void analytics.track({
        event: 'File - download',
        properties: {
          fileUuid,
        },
      });

      const { downloadUrl } = await mutateAsync({
        fileUuid,
        filename,
        preview: false,
      });

      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;

        document.body.appendChild(link);

        link.click();
      }
    },
    /**
     * Previews file in a new tab, non-previewable file types will be downloaded
     *
     * @param fileUuid the file uuid
     * @param filename an optional filename, defaults to the original filename
     */
    preview: async (fileUuid: string, filename?: string) => {
      // eslint-disable-next-line no-void
      void analytics.track({
        event: FILE_PREVIEW_ANALYTICS_EVENT,
        properties: {
          fileUuid,
        },
      });

      const { downloadUrl, isEmailLogger } = await mutateAsync({
        fileUuid,
        filename,
        preview: true,
      });

      if (isEmailLogger && options?.onPreviewEmailLogger) {
        options.onPreviewEmailLogger(fileUuid);

        return;
      }

      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
      return downloadUrl;
    },
    /**
     *
     * @param fileUuid
     * @returns The previewUrl of the file
     */
    previewUrl: async (fileUuid: string) => {
      const { downloadUrl } = await mutateAsync({
        fileUuid,
        preview: true,
      });
      return downloadUrl;
    },

    isDownloading: isLoading,
  };
}
